// frontend/src/hooks/useAutoScrollOnVisit.js
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Configurazione pagine
const SCROLL_CONFIG = {
    offsetPages: [
        'ilmondodeiviniabruzzese',
        'sceglierebeneivini',
        'degustazionedeivini',
        'provincedellabruzzo',
        'tipologiainabruzzo',
        'vitignoinabruzzo',
        'enoquizabruzzo',
        'simulatoresommelier',
        'sagredellabruzzo'
    ],
    topPages: ['negozioonline', 'profile', 'admin'],
    offsetValue: 373,
    duration: {
        desktopOffset: 1200,
        desktopTop: 400,
        mobile: 300
    }
};

/**
 * Scroll fluido con easing personalizzato
 * @param {number} targetY - Posizione target Y
 * @param {number} duration - Durata in millisecondi
 * @param {string} easing - Tipo di easing (default: 'easeOutCubic')
 */
function smoothScrollTo(targetY, duration = 500, easing = 'easeOutCubic') {
    const startY = window.scrollY;
    const difference = targetY - startY;
    const startTime = performance.now();
    
    // Funzioni di easing ottimizzate
    const easingFunctions = {
        linear: (t) => t,
        easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
        easeOutQuad: (t) => t * (2 - t),
        easeOutQuart: (t) => 1 - Math.pow(1 - t, 4),
        easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
        easeInOutQuint: (t) => t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2,
    };
    
    const easeFunction = easingFunctions[easing] || easingFunctions.easeOutCubic;
    
    const scroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        let progress = Math.min(elapsed / duration, 1);
        progress = easeFunction(progress);
        
        window.scrollTo(0, startY + difference * progress);
        
        if (progress < 1) {
            requestAnimationFrame(scroll);
        }
    };
    
    requestAnimationFrame(scroll);
}

export function useAutoScrollOnVisit(options = {}) {
    const location = useLocation();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
    // Monitora resize
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const { 
        enabled = true, 
        delay = 100,
        easing = 'easeOutCubic'
    } = options;

    useEffect(() => {
        if (!enabled) return;
        
        // Ignora se c'è un hash nell'URL
        if (window.location.hash) return;
        
        const path = location.pathname;
        const isDesktop = windowWidth > 800;
        
        let targetTop = 0;
        let duration = SCROLL_CONFIG.duration.mobile;
        let selectedEasing = easing;
        
        if (isDesktop) {
            const needsOffset = SCROLL_CONFIG.offsetPages.some(page => path.includes(page));
            
            if (needsOffset) {
                targetTop = SCROLL_CONFIG.offsetValue;
                duration = SCROLL_CONFIG.duration.desktopOffset;
                selectedEasing = 'easeOutCubic';
            } else {
                targetTop = 0;
                duration = SCROLL_CONFIG.duration.desktopTop;
                selectedEasing = 'easeOutQuad';
            }
        }
        
        // Schermi molto piccoli: scroll più veloce
        if (windowWidth <= 480) {
            duration = 300;
            selectedEasing = 'linear';
        }
        
        const timer = setTimeout(() => {
            // ✅ USA LA TUA FUNZIONE smoothScrollTo con easing personalizzato
            smoothScrollTo(targetTop, duration, selectedEasing);
        }, delay);
        
        return () => clearTimeout(timer);
        
    }, [location.pathname, location.key, windowWidth, enabled, delay, easing]);
    
    return { windowWidth, isDesktop: windowWidth > 800 };
}
